<template>
  <BaseModal
    :heading="header"
    small
    testId="sp-transaction"
    :unclosable="unclosable"
    @close="$emit('close')"
  >
    <div :class="$style.base">
      <PaymentTransition>
        <Flex
          v-if="status === 'default'"
          direction="column"
          align="center"
          justify="center"
          :class="$style.paymentStatus"
        >
          <div v-test="'pos-terminal-loading'">
            <div :class="$style.image">
              <img src="/img/salonized-pay/checkout/awaiting-payment.svg">
            </div>
          </div>
          <Flex
            direction="column"
            :gap="0.5"
          >
            <BaseHeading
              size="s"
              center
            >
              {{ $t('register.pos.amount') }}
            </BaseHeading>
            <BaseHeading
              size="xl"
              center
            >
              {{ filters.currency(amount) }}
            </BaseHeading>
          </Flex>
          <BaseText
            center
            v-test="'sp-transaction-loading-terminal'"
          >
            {{ $t('register.pos.loading') }}
          </BaseText>
          <BaseSpinner inline />
        </Flex>
      </PaymentTransition>
      <PaymentTransition>
        <Flex
          v-if="status === 'pending' || status === 'retry'"
          direction="column"
          align="center"
          justify="center"
          :class="$style.paymentStatus"
        >
          <img src="/img/salonized-pay/checkout/awaiting-transaction.svg">
          <Flex
            direction="column"
            :gap="0.5"
          >
            <BaseHeading
              size="s"
              center
            >
              {{ $t('register.pos.amount') }}
            </BaseHeading>
            <BaseHeading
              size="xl"
              center
            >
              {{ filters.currency(amount) }}
            </BaseHeading>
          </Flex>
          <BaseText v-test="'sp-transaction-awaiting'">
            {{ $t('register.pos_awaiting_transaction.description') }}
          </BaseText>
          <BaseSpinner inline />
        </Flex>
      </PaymentTransition>
      <PaymentTransition>
        <Flex
          v-if="status === 'waiting'"
          direction="column"
          align="center"
          justify="center"
          :class="$style.paymentStatus"
        >
          <img src="/img/salonized-pay/checkout/awaiting-transaction.svg">
          <Flex
            direction="column"
            :gap="0.5"
          >
            <BaseHeading
              size="s"
              center
            >
              {{ $t('register.pos.amount') }}
            </BaseHeading>
            <BaseHeading
              size="xl"
              center
            >
              {{ filters.currency(amount) }}
            </BaseHeading>
          </Flex>
          <BaseText v-test="'sp-transaction-waiting'">
            {{ $t('register.pos_still_processing_transaction.description') }}
          </BaseText>
          <BaseSpinner inline />
        </Flex>
      </PaymentTransition>
      <PaymentTransition>
        <Flex
          v-if="status === 'success'"
          direction="column"
          align="center"
          justify="center"
          :class="$style.paymentStatus"
        >
          <Confetti
            v-if="tipAmount"
            v-test="'sp-transaction-success-confetti'"
          />
          <img src="/img/salonized-pay/checkout/successful-payment.svg">
          <BaseText
            size="l"
            bold
            v-test="'sp-transaction-success'"
          >
            {{ $t('register.pos_successful_transaction.description') }}
          </BaseText>
          <BaseAlert
            v-if="tipAmount && selectedCustomer"
            v-test="'sp-transaction-success-tip'"
            color="success"
            :text="`🎉 ${$t('register.pos_successful_transaction.tip_message', { client: selectedCustomer?.firstName, amount: filters.currency(tipAmount) })}`"
          />
        </Flex>
      </PaymentTransition>
      <PaymentTransition>
        <Flex
          v-if="status === 'failed'"
          direction="column"
          align="center"
          justify="center"
          :class="$style.paymentStatus"
        >
          <template v-if="refusalReason === 'CONVERTER_ERROR_REQUEST'">
            <img
              src="/img/salonized-pay/checkout/failed-payment-connection-lost.svg"
            >
            <BaseText v-test="'sp-transaction-failed-connection-lost'">
              {{ $t(
                  'register.pos_failed_transaction.description_connection_lost'
                ) }}
            </BaseText>
          </template>
          <template v-else-if="refusalReason === 'NOT_ENOUGH_BALANCE'">
            <img
              src="/img/salonized-pay/checkout/failed-payment-card-declined.svg"
            >
            <BaseText v-test="'sp-transaction-failed-card-declined'">
              {{ $t('register.pos_failed_transaction.description_card_declined') }}
            </BaseText>
          </template>
          <template v-else-if="refusalReason === 'DECLINED'">
            <img src="/img/salonized-pay/checkout/failed-payment-cancelled.svg">
            <BaseText v-test="'sp-transaction-failed-cancelled'">
              {{ $t('register.pos_failed_transaction.description_cancelled') }}
            </BaseText>
          </template>
          <template v-else-if="refusalReason === 'CARD_EXPIRED'">
            <img
              src="/img/salonized-pay/checkout/failed-payment-card-declined.svg"
            >
            <BaseText v-test="'sp-transaction-failed-card-expired'">
              {{ $t('register.pos_failed_transaction.description_card_expired') }}
            </BaseText>
          </template>
          <template v-else>
            <img src="/img/salonized-pay/checkout/failed-payment.svg">
            <BaseText v-test="'sp-transaction-failed'">
              {{ $t('register.pos_failed_transaction.description') }}
            </BaseText>
          </template>
        </Flex>
      </PaymentTransition>
    </div>
    <template
      v-if="status === 'failed'"
      #footer
    >
      <PaymentTransition>
        <Flex :direction="$screen !== 's' ? 'row' : 'column'">
          <BaseButton
            color="inverted"
            @click="$emit('close')"
          >
            {{ $t('register.salonized_pay.payment.failed.other') }}
          </BaseButton>
          <BaseButton
            @click="$emit('retry')"
            v-test="'sp-transaction-retry'"
          >
            {{ $t('register.pos_failed_transaction.try_again') }}
          </BaseButton>
        </Flex>
      </PaymentTransition>
    </template>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Confetti from '@/components/Confetti.vue';
import filters from '@/filters';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import type { PosPaymentRefusalReason } from '@/types';
import PaymentTransition from './PaymentTransition.vue';

const props = defineProps<{
  amount: number;
  status: string;
  refusalReason: PosPaymentRefusalReason | null;
  tipAmount: number;
}>();

const { selectedCustomer } = storeToRefs(useRegisterOrderStore());

const { t } = useI18n();
defineEmits(['close', 'retry']);

const unclosable = computed(
  () => props.status !== 'success' && props.status !== 'failed',
);

let pushCount = 0;

const onPopState = () => {
  window.history.pushState(null, '', window.location.href);
  pushCount++;
};

const cleanupBackGuard = () => {
  window.removeEventListener('popstate', onPopState);

  if (pushCount > 0) {
    window.history.go(-pushCount);
    pushCount = 0;
  }
};

watch(
  unclosable,
  (isUnclosable) => {
    if (isUnclosable) {
      window.history.pushState(null, '', window.location.href);
      pushCount++;
      window.addEventListener('popstate', onPopState);
    } else {
      cleanupBackGuard();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  cleanupBackGuard();
});

const header = computed(() => {
  switch (props.status) {
    case 'pending':
    case 'retry':
    case 'waiting':
      return t('register.salonized_pay.pos.headers.complete');
    case 'success':
      return t('register.salonized_pay.pos.headers.success');
    case 'failed':
      switch (props.refusalReason) {
        case 'CONVERTER_ERROR_REQUEST':
          return t('register.salonized_pay.pos.headers.failed_connection_lost');
        case 'NOT_ENOUGH_BALANCE':
        case 'CARD_EXPIRED':
          return t('register.salonized_pay.pos.headers.failed_card_declined');
        case 'DECLINED':
          return t('register.salonized_pay.pos.headers.failed_cancelled');
        default:
          return t('register.salonized_pay.pos.headers.failed');
      }
    default:
      return t('register.salonized_pay.pos.headers.waiting');
  }
});
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
  height: 350px;
}

.image {
  display: flex;
  justify-content: center;
}

.paymentStatus {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding-bottom: $spacing;
  text-align: center;

  img {
    max-height: 70%;
    max-width: 50%;
  }
}
</style>
